import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { isBefore } from 'date-fns';
import { SHORT_PLAIN_TEXT_200_LENGTH } from '../../../../../../constants/common.constants';
import { MILLISECONDS_IN_DAY } from '../../../../../../constants/date-time.constants';
import { utcToZonedTime } from '../../../../../../infrastructure/utils/date-fns-overrides.utils';
import { AudioBridgeProvider, ConferenceModes, IAudioBridgeDTO } from '../../../../../../api/connections.dao';
import { AUDIO_BRIDGE_MAX_LENGTH_ERROR, AUDIO_BRIDGE_TEXT_ERROR, DURATION_ERROR_TEXT, START_TIME_ERROR_TEXT, START_TIME_PAST_TEXT, TIME_FIELD_ERROR_TEXT } from '../../../../../../constants/audio-bridge-connection.constant';
import { IZoomDAO, IZoomTelephoneNumberSetDTO } from '../../../../../../api/zoom.dao';
import { COMMON_ITEMS_PER_PAGE } from '../../../../../../constants/pagination.contants';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { AudioBridgeProviderLabel } from '../../../../../../infrastructure/utils/common.utils';
class CreateAudioBridgeFormDataVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @IoC.Inject private readonly zoomDao: IZoomDAO;
    @mobx.observable
    private timeZone: string;
    @mobx.observable
    private providerName: string = AudioBridgeProvider.TURBOBRIDGE;
    @mobx.observable
    private conferenceMode: string = ConferenceModes.Hosts;
    @mobx.observable
    private audioBridgeName: string;
    @mobx.observable
    private autoSyncEnabled: boolean;
    @mobx.observable
    private automaticallyRecordEnabled: boolean;
    @mobx.observable
    private startDateTime: Date;
    @mobx.observable
    private endDateTime: Date;
    @mobx.observable
    public validationErrors: Record<string, JSX.Element>;
    @mobx.observable
    private telephoneNumberSets: { value: string; label: string }[] = [];
    @mobx.observable
    public isLoading: boolean = false;
    @mobx.observable
    public audioProviderId: string;

    constructor(
        private readonly eventTimeZone: string,
        private readonly meetingStartDateTime: Date,
        private readonly meetingEndDateTime: Date,
        private readonly connectionsName: string,
    ) {
        this.cleanup();
        this.init();
    }

    public getStartMinDate = (): Date => {
        return utcToZonedTime(new Date(), this.timeZone);
    };

    public getStartDateTime = (): Date => {
        return this.startDateTime;
    };

    @mobx.action
    public setStartDateTime = (startDateTime: Date): void => {
        this.startDateTime = startDateTime;
        this.checkDates();
    };

    public getEndDateTime = (): Date => {
        return this.endDateTime;
    };

    @mobx.action
    public setEndDateTime = (endDateTime: Date): void => {
        this.endDateTime = endDateTime;
        this.checkDates();
    };

    public getProvider = (): string => {
        return this.providerName;
    };

    @mobx.action
    public setProvider = (provider: string): void => {
        this.providerName = provider;
    };

    public getConferenceMode = (): string => {
        return this.conferenceMode;
    };

    @mobx.action
    public setConferenceMode = (mode: string): void => {
        this.conferenceMode = mode;
    };

    public getTimeZone = (): string => {
        return this.timeZone;
    };

    @mobx.action
    public setTimeZone = (timeZone: string): void => {
        this.timeZone = timeZone;
    };

    public getAudioBridgeName = (): string => {
        return this.audioBridgeName;
    };

    @mobx.action
    public setAudioBridgeName = (audioBridgeName: string): void => {
        this.audioBridgeName = audioBridgeName;
    };

    public getAutoSyncEnabled = (): boolean => {
        return this.autoSyncEnabled;
    };

    @mobx.action
    public toggleAutoSyncEnabled = (): void => {
        this.autoSyncEnabled = !this.autoSyncEnabled;
    };

    public getAutomaticallyRecordEnabled = (): boolean => {
        return this.automaticallyRecordEnabled;
    };

    @mobx.action
    public toggleAutomaticallyRecordEnabled = (): void => {
        this.automaticallyRecordEnabled = !this.automaticallyRecordEnabled;
    };

    public getFieldError = (fieldName: string): JSX.Element | undefined => {
        return this.validationErrors[fieldName];
    };

    @mobx.action
    public checkForm(): boolean {
        this.checkAudioBridgeName();
        this.checkDates();
        return !this.validationErrors.audioBridgeName && !this.validationErrors.dates && !this.validationErrors.description;
    }

    @mobx.action
    public getAPIDTO = (): IAudioBridgeDTO => {
        const conferenceModeMapping: { [key: string]: string } = ConferenceModes;
        const normalizedConferenceMode = conferenceModeMapping[this.conferenceMode] || this.conferenceMode.toUpperCase();

        const dataToDTO: IAudioBridgeDTO = {
            audioProviderId: this.audioProviderId,
            name: this.audioBridgeName,
            timeZone: this.timeZone,
            endTime: this.endDateTime,
            startTime: this.startDateTime,
            conferenceMode: normalizedConferenceMode.toUpperCase(),
            provider: this.providerName.toUpperCase(),
            recordCalls: this.automaticallyRecordEnabled
        };
        return dataToDTO;
    };

    @mobx.action
    public cleanup = (): void => {
        this.timeZone = this.eventTimeZone;
        this.providerName = AudioBridgeProvider.TURBOBRIDGE;
        this.audioBridgeName = this.connectionsName;
        this.conferenceMode = ConferenceModes.Hosts;
        this.startDateTime = this.meetingStartDateTime || null;
        this.endDateTime = this.meetingEndDateTime || null;
        this.validationErrors = {};
        this.audioProviderId = this.telephoneNumberSets[0]?.value?.toString();
        this.autoSyncEnabled = true;
        this.automaticallyRecordEnabled = true;
    };

    @mobx.action
    public checkAudioBridgeName(): void {
        if (this.validationErrors.audioBridgeName)
            delete this.validationErrors.audioBridgeName;
        if (this.audioBridgeName) {
            this.audioBridgeName = this.audioBridgeName.trim();
        }
        if (!this.audioBridgeName) {
            this.validationErrors.audioBridgeName = AUDIO_BRIDGE_TEXT_ERROR;
        }
        if (this.audioBridgeName.length > SHORT_PLAIN_TEXT_200_LENGTH) {
            this.validationErrors.audioBridgeName = AUDIO_BRIDGE_MAX_LENGTH_ERROR;
            return;
        }
    }

    @mobx.action
    public checkDates(): void {
        if (this.validationErrors.dates)
            delete this.validationErrors.dates;

        if (!this.startDateTime || !this.endDateTime) {
            this.validationErrors.dates = TIME_FIELD_ERROR_TEXT;
            return;
        }
        if (!isBefore(this.startDateTime, this.endDateTime)) {
            this.validationErrors.dates = START_TIME_ERROR_TEXT;
            return;
        }
        if (isBefore(this.startDateTime, this.getStartMinDate())) {
            this.validationErrors.dates = START_TIME_PAST_TEXT;
            return;
        }
        if ((this.endDateTime.getTime() - this.startDateTime.getTime()) > MILLISECONDS_IN_DAY) {
            this.validationErrors.dates = DURATION_ERROR_TEXT;
            return;
        }
    }

    public parseTelephoneNumberSetOptions(telephoneNumberSets: IZoomTelephoneNumberSetDTO[]): { value: string; label: string }[] {
        let telephoneNumberSetData = telephoneNumberSets.reduce((acc, val) => {
            if (val.provider === AudioBridgeProvider.TURBOBRIDGE) {
                acc.push({
                    value: val.id,
                    label: `${AudioBridgeProviderLabel[val.provider]}: ${val.name}`
                });
            }
            return acc;
        }, [])
        .sort((prev, next) => prev.label.localeCompare(next.label));
        this.audioProviderId = telephoneNumberSetData[0]?.value?.toString();
        return telephoneNumberSetData;
    }

    public getTelephoneNumberSetOptions(): { value: string; label: string }[] {
        return this.telephoneNumberSets;
    }

    @mobx.action
    public async fetchTelephoneNumberSets(): Promise<void> {
        try {
            mobx.runInAction(() => {
                this.isLoading = true;
            });
            const telephoneNumberSetsResponse =
                await this.zoomDao.getTelephoneNumberSets({
                    page: 0,
                    size: COMMON_ITEMS_PER_PAGE,
                    query: '',
                    sortField: null,
                    sortOrder: null
                });
            mobx.runInAction(() => {
                this.telephoneNumberSets = this.parseTelephoneNumberSetOptions(telephoneNumberSetsResponse.content);
            });
        } catch (error) {
            CreateAudioBridgeFormDataVM.handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    public setAudioProviderId(value: string): void {
        this.audioProviderId = value;
    }

    private async init(): Promise<void> {
        await this.fetchTelephoneNumberSets();
    }
}

export { CreateAudioBridgeFormDataVM };